import React, { useMemo } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTranslation } from 'react-i18next';
import { Share } from 'lucide-react';
import { Button } from '@artstorefronts/ui';
import { parseCommunityDiscussionToolOutput } from '@arthelper/shared';
import { remarkDashRule } from '@arthelper/shared/chat-markdown';
import CopyButton from '../CopyButton';
import ChatPartRating from './ChatPartRating';
import MessagePart from './MessagePart';
import MessageTimestamp from './MessageTimestamp';
import Thinking from './Thinking';
import { markdownComponents as components } from './tool-renderers/common';
import { TOOL_RENDERERS } from './tool-renderers';
import ToolCallWrapper from './ToolCall';
import { useChatUI } from '~/context/chatUI';
import { getToolCallFromPart } from './toolPartAdapter';
import { sameOtherProps } from './utils/memoProps';

const COMMUNITY_DISCUSSIONS_TOOL = 'search_community_discussions';
const UPLOAD_IMAGE_TOOL = 'upload_image';
const REMARK_PLUGINS = [remarkGfm, remarkDashRule];

function isActing(toolName: string) {
  return TOOL_RENDERERS[toolName]?.type === 'acting';
}

// v5 UIMessage: use .parts only. Reasoning is deliberately excluded — it is the model's
// chain of thought, never the answer, and it is not rendered, copied or shared anywhere.
function getMessageDisplayText(message: any): string {
  if (!message.parts?.length) {
    return typeof message.content === 'string' ? message.content : '';
  }
  return message.parts
    .filter((p: any) => p.type === 'text')
    .map((p: any) => p.text ?? '')
    .join('\n\n');
}

// Separate tools by type. Reasoning parts are counted, never collected: the header says the
// assistant thought, the chain of thought itself never reaches the transcript.
function groupParts(parts: any[]) {
  return parts.reduce(
    (acc: any, part: any) => {
      const toolCall = getToolCallFromPart(part);
      if (toolCall) {
        if (isActing(toolCall.toolName)) {
          acc.acting.push(toolCall);
        } else {
          acc.thinking.push(toolCall);
        }
        return acc;
      }

      if (part.type === 'reasoning') {
        acc.hasReasoningParts = true;
        return acc;
      }

      if (part.type !== 'step-start') {
        acc.other.push(part);
      }
      return acc;
    },
    { acting: [], thinking: [], other: [], hasReasoningParts: false }
  );
}

function AssistantMessage({
  message,
  status,
  isLast,
  onShareChat,
  conversationId,
  onUploadComplete,
}: {
  message: any;
  status: string;
  isLast: boolean;
  onShareChat?: () => void;
  conversationId?: string;
  onUploadComplete?: any;
}) {
  const { t } = useTranslation('chat');
  const { showRating } = useChatUI();
  // Recomputed only when the message itself changes, not on every context-driven re-render.
  const displayText = useMemo(() => getMessageDisplayText(message), [message.parts, message.content]);
  const { acting, thinking, other, hasReasoningParts } = useMemo(
    () => groupParts(message.parts || []),
    [message.parts]
  );

  if (message.role !== 'assistant') {
    return null;
  }

  const hasTextResponse = other.some((part: any) => part.type === 'text' && part.text?.trim());

  // Get all text content for copying (fallback to derived display text)
  const getAllTextContent = () => {
    const textParts = other
      .filter((part: any) => part.type === 'text' && part.text?.trim())
      .map((part: any) => part.text)
      .join('\n\n');

    return textParts || displayText || '';
  };

  const textContent = getAllTextContent();

  const actingTrailingCommunity = acting.filter((t: any) => t.toolName === COMMUNITY_DISCUSSIONS_TOOL);

  // upload_image is type "acting" and renders inline above text; the model sometimes also
  // emits <ShowUpload /> in the text reply — keep a single uploader per message.
  const hasUploadImageActing = acting.some((t: any) => t.toolName === UPLOAD_IMAGE_TOOL);
  let uploadImageActingRendered = false;
  const actingBeforeOther = acting.filter((t: any) => {
    if (t.toolName === COMMUNITY_DISCUSSIONS_TOOL) return false;
    if (t.toolName === UPLOAD_IMAGE_TOOL) {
      if (uploadImageActingRendered) return false;
      uploadImageActingRendered = true;
    }
    return true;
  });

  // Whether the run is still producing this turn. A failed turn is NOT active — the thinking
  // header has to settle rather than spin forever — but it is not finished either, which is why
  // this differs from the button gate below.
  const isRunActive = status === 'submitted' || status === 'streaming' || status === 'tool-calls';

  // One in-flight indicator, in one place, for the whole wait: it appears the moment the turn
  // starts — the gap the standalone dots used to cover — instead of being swapped for a different
  // control partway through, which is what made the transcript jump mid-think.
  //
  // A turn that called a tool or reasoned keeps its header for good and settles into "Thought
  // for…". A plain text answer has nothing to report, so the header gives way to the text as soon
  // as the first of it arrives, rather than spinning "Thinking…" above an answer already on
  // screen and then vanishing when the run ends.
  const showThinking = thinking.length > 0 || hasReasoningParts || (isLast && isRunActive && !hasTextResponse);

  // Render community discussion cards after the assistant text so links stay visible (other acting tools stay above text)
  const groupedParts = [
    ...(showThinking ? [{ type: 'thinking', tools: thinking, hasTextResponse }] : []),
    ...actingBeforeOther.map((tool: any) => ({ type: 'acting', tool })),
    ...other,
    ...actingTrailingCommunity.map((tool: any) => ({ type: 'acting', tool })),
  ];

  // Only a finished message gets rated, copied or shared. An earlier message in the transcript is
  // finished by definition; the last one only once the run reaches `ready`. `submitted`,
  // `streaming`, `tool-calls` and `error` all mean there is no complete response to act on yet —
  // which is why the `loading-message` placeholder and a failed turn show no buttons either.
  const shouldShowButtons = !isLast || status === 'ready';

  return (
    <div className="py-4">
      <div className="w-full">
        {groupedParts.length > 0 && (
          <>
            {groupedParts.map((part: any, idx: number) => {
              if (part.type === 'thinking') {
                return (
                  <Thinking
                    key={`thinking-${idx}`}
                    toolCalls={part.tools}
                    isComplete={!isLast || !isRunActive}
                    messageId={message.id}
                    hasTextResponse={part.hasTextResponse}
                  />
                );
              }
              if (part.type === 'acting') {
                const isCommunityBridge = part.tool?.toolName === COMMUNITY_DISCUSSIONS_TOOL;
                // Hold the cards back only while the run is still producing the turn. A failed
                // turn is not `ready` either, but its tool results arrived before it failed and
                // are worth keeping on screen.
                if (isCommunityBridge && isLast && isRunActive) {
                  return null;
                }
                if (isCommunityBridge && !parseCommunityDiscussionToolOutput(part.tool?.result).hasResults) {
                  return null;
                }
                return (
                  <div key={`acting-${idx}`} className="mt-4 mb-4">
                    {isCommunityBridge ? (
                      <p className="text-sm text-gray-800 font-medium mt-6 mb-1 leading-snug">
                        {t('tool.search-community-discussions.section-intro')}
                      </p>
                    ) : null}
                    <ToolCallWrapper
                      toolCall={part.tool}
                      onUploadComplete={part.tool?.toolName === UPLOAD_IMAGE_TOOL ? onUploadComplete : undefined}
                      uploadAckRetryKey={part.tool?.toolName === UPLOAD_IMAGE_TOOL ? status : undefined}
                    />
                  </div>
                );
              }
              return (
                <MessagePart
                  key={part.type + idx}
                  part={part}
                  messageId={message.id}
                  idx={idx}
                  isStreaming={isLast && status === 'streaming'}
                  onUploadComplete={onUploadComplete}
                  suppressShowUpload={hasUploadImageActing}
                  uploadAckRetryKey={status}
                />
              );
            })}
          </>
        )}

        {/* Fallback content rendering (v5: derive from parts when no grouped parts) */}
        {groupedParts.length === 0 && displayText && (
          <div className="text-gray-800">
            <Markdown components={components} remarkPlugins={REMARK_PLUGINS}>
              {displayText}
            </Markdown>
          </div>
        )}

        {/* Action buttons. The timestamp lives in this row because the row is gated by
            `shouldShowButtons`, which is false for the synthetic `loading-message` placeholder and
            while streaming — so a message that has not arrived yet never shows a time. */}
        <div className={`my-6 flex items-center gap-2 ${!shouldShowButtons ? 'hidden' : ''}`}>
          {showRating && (
            <ChatPartRating chatPartId={message.id} initialGood={message.good} initialNotes={message.notes} />
          )}

          {textContent && <CopyButton text={textContent} showText={false} chatItem={true} />}

          {onShareChat && conversationId && (
            <Button
              className="px-3 py-1.5 h-auto text-xs rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors font-medium"
              variant="ghost"
              onClick={onShareChat}
              title={t('message.share-conversation')}
            >
              <Share className="w-4 h-4 mr-1.5" />
              {t('message.share-this-chat')}
            </Button>
          )}

          <MessageTimestamp message={message} />
        </div>
      </div>
    </div>
  );
}

export default React.memo(AssistantMessage, (prevProps, nextProps) => {
  if (nextProps.isLast) {
    return false; // Always re-render the last message during streaming
  }

  // Callbacks, conversation id and status first: the share button and upload card read them, and
  // a stale one would keep an old share target on an unchanged row.
  if (!sameOtherProps(prevProps, nextProps)) {
    return false;
  }

  // Same message object → the id, text and parts checks below are equal by construction. Skipping
  // them keeps a re-render of a long transcript O(rows) instead of re-serialising every row's parts.
  if (prevProps.message === nextProps.message) {
    return true;
  }

  if (prevProps.message.id !== nextProps.message.id) {
    return false;
  }

  // A rebuilt message object that kept its parts array (and content) renders identically.
  if (prevProps.message.parts === nextProps.message.parts && prevProps.message.content === nextProps.message.content) {
    return true;
  }

  return (
    getMessageDisplayText(prevProps.message) === getMessageDisplayText(nextProps.message) &&
    JSON.stringify(prevProps.message.parts) === JSON.stringify(nextProps.message.parts)
  );
});
